import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import css from './PreferencesSection.scss';
import Icon from 'SF/MaterialIcon';
import { isStringOverCharLimit } from './profileValidation';

/**
 * Provides either a read-only "fake" input to display the user's pronouns or an input object that
 * allows them to edit and save their pronouns as part of the user profile.
 * @param {Boolean} isIE whether to apply IE CSS tweaks
 * @param {Boolean} isEditable whether users are able to edit their pronouns
 * @param {Boolean} isStaff whether this profile is loading for a staff member
 * @param {String} pronouns the current user's pronouns
 * @param {Function} setValue saves any updates made to the pronouns
 * @param {Boolean} showPronouns whether pronoun section should be displayed or not
 * @param {Boolean} staffHasStudentRole whether a staff member also has a student role
 * @returns {JSX.Element|null} the pronoun section of the student profile, or a null value if it should not be displayed
 */
const Pronouns = ({ isIE, isEditable, isStaff, pronouns, setValue, showPronouns, staffHasStudentRole }) => {
    // Only students and staff members with a student role can see this section when enabled
    if (!showPronouns || (isStaff && !staffHasStudentRole)) return null;

    const getSectionLabel = () => isStaff ? 'Pronouns for Your Student Profile' : 'Pronouns';

    const validatePronounLength = () => {
        if (isStringOverCharLimit(pronouns, 25)) {
            return <span className={css['error-message']} aria-hidden={true}>
                <Icon icon="error" aria-hidden="true" />{pronouns.length - 25} characters over 25 character limit.
            </span>
        }
    }

    const getPronounInputs = () => {
        if (!isEditable) {
            return <div className={css['fake-input'] + ' disabled'} id="pronouns-section">
                    <span
                        aria-labelledby="pronouns-input"
                        className={classnames(css['overflow-fade'], { [css.ie]: isIE })}
                    >
                        {pronouns}
                    </span>
                <Icon icon="lock" aria-hidden={true} />
            </div>;
        }
        return <input type="text"
                      aria-labelledby="pronouns-input"
                      onChange={(e) => setValue({ pronouns: e.target.value }) }
                      placeholder={'Ex: she/her/them'}
                      value={pronouns} />
    }

    const getHelpText = () => {
        return <span className={css['profile-sub-text']}>
            Helps your staff connections know how to refer to you.
            { isEditable && <span className={css['no-line-wrap']}> Ex: she/her/them</span> }
        </span>
    }

    return <div className={css['title-section']}>
        <div className={classnames({[css.error]: isStringOverCharLimit(pronouns, 25)})}>
            <label id="pronouns-input" aria-hidden={true}>{ getSectionLabel() }</label>
            <div>{ getPronounInputs() }</div>
            { isEditable && validatePronounLength() }
        </div>
        { getHelpText() }
    </div>;
};

Pronouns.defaultProps = {
    isEditable: false,
    isStaff: false,
    staffHasStudentRole: false
}

Pronouns.propTypes = {
    isEditable: PropTypes.bool,
    isIE: PropTypes.bool,
    isStaff: PropTypes.bool,
    pronouns: PropTypes.string,
    setValue: PropTypes.func,
    staffHasStudentRole: PropTypes.bool,
    showPronouns: PropTypes.bool
};

export default Pronouns;
